import React, { useEffect, useMemo, useState } from "react";
import styles from "./PartnerNavSlider.module.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
// import SplashScreen from "../../SplashScreen";

const API_BASE_URL = "https://coinyoco.world/brobizee-api";
const API_USER_URL = `${API_BASE_URL}/get_user.php`;

export default function PartnerNavSlider({
  isOpen = true,
  onClose,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // =====================================================
  // ROUTE PARAMETERS
  // =====================================================

  const {
    userId: routeUserId,
    deviceUuid: routeDeviceUuid,
  } = useParams();

  // =====================================================
  // LOCAL STORAGE
  // =====================================================

  const localUserId = localStorage.getItem("user_id");
  const localDeviceUuid = localStorage.getItem("device_uuid");

  // =====================================================
  // FINAL USER ID + DEVICE UUID
  // Route parameter first, localStorage fallback
  // =====================================================

  const userId = routeUserId || localUserId;
  const deviceUuid = routeDeviceUuid || localDeviceUuid;

  // =====================================================
  // STATES
  // =====================================================

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // =====================================================
  // USER NOT FOUND STATE (NEW)
  // =====================================================

  const [userNotFound, setUserNotFound] = useState(false);

  // =====================================================
  // ERROR POPUP STATE (NEW)
  // =====================================================

  const [showNetworkError, setShowNetworkError] = useState(false);
  const [networkErrorMessage, setNetworkErrorMessage] = useState("");

  // =====================================================
  // SHOW ERROR POPUP (NEW)
  // =====================================================

  const showErrorPopup = (message) => {
    setNetworkErrorMessage(
      message || "Something went wrong. Please try again."
    );
    setShowNetworkError(true);
  };

  // =====================================================
  // CLOSE ERROR POPUP (NEW)
  // =====================================================

  const closeErrorPopup = () => {
    setShowNetworkError(false);
    setNetworkErrorMessage("");
  };

  // =====================================================
  // HANDLE GO HOME (NEW)
  // =====================================================

  const handleGoHome = () => {
    try {
      localStorage.removeItem("user_id");
      localStorage.removeItem("device_uuid");
      localStorage.removeItem("user_name");
      localStorage.removeItem("user_address");
      localStorage.removeItem("profile_user");
      localStorage.removeItem("profile_image_url");
      localStorage.removeItem("current_latitude");
      localStorage.removeItem("current_longitude");
      localStorage.removeItem("current_address");
    } catch (e) {
      console.warn("Clear user data failed:", e);
    }

    setShowNetworkError(false);
    setNetworkErrorMessage("");

    if (onClose) {
      onClose();
    }

    navigate("/", { replace: true });
  };

  // =====================================================
  // BASE PATH
  // =====================================================

  const basePath =
    userId && deviceUuid
      ? `/${userId}/${deviceUuid}`
      : "";

  // =====================================================
  // NAVIGATION ITEMS
  // =====================================================

  const navItems = useMemo(
    () => [
      {
        label: "Profile",
        path: "/partner_edit_Profile",
        icon: (
          <svg
            width="21"
            height="21"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21c0-4.2 3.6-7 8-7s8 2.8 8 7" />
          </svg>
        ),
      },

      {
        label: "Privacy Policy",
        path: "/privacy-policy",
        icon: (
          <svg
            width="21"
            height="21"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
        ),
      },

      {
        label: "Contact Us",
        path: "/contact-us",
        icon: (
          <svg
            width="21"
            height="21"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
            <path d="M8 9h8" />
            <path d="M8 13h5" />
          </svg>
        ),
      },

      {
        label: "Help & Support",
        path: "/help-support",
        icon: (
          <svg
            width="21"
            height="21"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M9.5 9a2.7 2.7 0 1 1 4.6 1.9c-.9.9-2.1 1.3-2.1 2.6" />
            <circle cx="12" cy="17" r="1" />
          </svg>
        ),
      },
    ],
    []
  );

  // =====================================================
  // ACTIVE NAV ITEM
  // Automatically detects current URL
  // =====================================================

  const getActiveItem = () => {
    const currentPath = location.pathname;

    const matchedItem = navItems.find((item) => {
      const completePath = basePath
        ? `${basePath}${item.path}`
        : item.path;

      return (
        currentPath === completePath ||
        currentPath.startsWith(`${completePath}/`)
      );
    });

    return matchedItem?.label || "";
  };

  const activeItem = getActiveItem();

  // =====================================================
  // NAVIGATION FUNCTION
  // =====================================================

  const goTo = (path) => {
    const targetPath = basePath
      ? `${basePath}${path}`
      : path;

    navigate(targetPath);

    // Close drawer after navigation
    if (onClose) {
      onClose();
    }
  };

  // =====================================================
  // NAV ITEM CLICK
  // =====================================================

  const handleNavClick = (item) => {
    goTo(item.path);
  };

  // =====================================================
  // LOGOUT
  // =====================================================

  const handleLogout = () => {

    if (onClose) {

      onClose();

    }


    /* -------------------------------------------------------
       Preserve user/device URL
    ------------------------------------------------------- */

    if (
      userId &&
      deviceUuid
    ) {

      navigate(
        `/${userId}/${deviceUuid}/userandpartner`,
        {
          replace: true
        }
      );

    } else {

      navigate(
        "/userandpartner",
        {
          replace: true
        }
      );

    }

  };

  // =====================================================
  // FETCH USER DATA
  // =====================================================

  useEffect(() => {
    let isMounted = true;

    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError("");
        setUserNotFound(false);   // NEW

        if (!userId) {
          if (isMounted) {
            setError("User ID not found in localStorage");
            setLoading(false);
          }

          return;
        }

        console.log("Fetching user:", userId);

        const response = await fetch(
          `${API_USER_URL}?user_id=${encodeURIComponent(userId)}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        const responseText = await response.text();

        console.log(
          "RAW GET USER RESPONSE:",
          responseText
        );

        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}: ${responseText}`
          );
        }

        let data;

        try {
          data = JSON.parse(responseText);
        } catch (jsonError) {
          console.error(
            "Invalid JSON:",
            responseText
          );

          throw new Error(
            "PHP API returned invalid JSON"
          );
        }

        console.log(
          "GET USER RESULT:",
          data
        );

        if (!isMounted) {
          return;
        }

        if (data.success && data.user) {
          setUser(data.user);
        } else {
          // NEW: mark user not found + show popup
          setUserNotFound(true);

          const msg =
            data.message ||
            "Unable to fetch user data";

          setError(msg);
          showErrorPopup(msg);
        }
      } catch (fetchError) {
        console.error(
          "Fetch user error:",
          fetchError
        );

        if (isMounted) {
          const msg =
            fetchError.message ||
            "Something went wrong while loading user data";

          setError(msg);
          showErrorPopup(msg);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserData();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  // =====================================================
  // ESC KEY SUPPORT
  // =====================================================

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && isOpen) {
        if (onClose) {
          onClose();
        }
      }
    };

    document.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [isOpen, onClose]);

  // =====================================================
  // PREVENT BODY SCROLL WHEN SLIDER OPEN
  // =====================================================

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [isOpen]);

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div className="show-data-page">
        <div className="show-data-card">
          {/* <SplashScreen /> */}
        </div>
      </div>
    );
  }

  // =====================================================
  // ERROR
  // =====================================================

  if (error) {
    return (
      <>
        <div className="show-data-page">
          <div className="show-data-card">
            <h2>Error</h2>

            <p>{error}</p>

            <button
              type="button"
              onClick={() => {
  handleGoHome();   // <-- parentheses = call it
}}
            >
              Retry
            </button>

            {/* NEW BUTTON — only when user not found */}
            {/* {userNotFound && (
              <button
                type="button"
                onClick={handleGoHome}
                style={{
                  marginTop: "12px",
                  background: "#FF6B00",
                  color: "#005CA9",
                  border: "none",
                  borderRadius: "10px",
                  padding: "11px 16px",
                  fontSize: "14px",
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  width: "100%",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Go to Home / Register
              </button>
            )} */}
          </div>
        </div>

        {/* ERROR POPUP */}
        {showNetworkError && (
          <div
            className="network-screen-wrapper"
            onClick={closeErrorPopup}
          >
            <div
              className="network-modal-card"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="network-header">
                <div className="network-icon">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#005CA9"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="9" y1="9" x2="9.01" y2="9" strokeWidth="3" />
                    <line x1="15" y1="9" x2="15.01" y2="9" strokeWidth="3" />
                    <line x1="9" y1="15" x2="15" y2="15" />
                  </svg>
                </div>

                <h2 className="network-title">Error</h2>
              </div>

              <p className="network-message">
                {networkErrorMessage}
              </p>

              <button
                type="button"
                className="network-ok-btn"
                onClick={closeErrorPopup}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {/* ERROR POPUP CSS */}
        <style>{`
          .network-screen-wrapper {
            position: fixed;
            inset: 0;
            z-index: 99999;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 10px;
            box-sizing: border-box;
            background: rgba(0, 0, 0, 0.28);
            backdrop-filter: blur(7px);
            -webkit-backdrop-filter: blur(7px);
            font-family:
              -apple-system,
              BlinkMacSystemFont,
              "SF Pro Text",
              "Segoe UI",
              Roboto,
              sans-serif;
          }

          .network-modal-card {
            width: 88%;
            max-width: 370px;
            background: rgba(255, 255, 255, 0.98);
            border-radius: 20px;
            padding: 28px 22px;
            text-align: center;
            box-sizing: border-box;
            box-shadow: 0 12px 35px rgba(0, 0, 0, 0.14);
            animation: networkPopup 0.28s cubic-bezier(0.16, 1, 0.3, 1);
          }

          .network-header {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 10px;
            margin-bottom: 14px;
          }

          .network-icon {
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .network-title {
            margin: 0;
            font-size: 20px;
            font-weight: 700;
            color: #111;
          }

          .network-message {
            margin: 0 auto 24px;
            max-width: 290px;
            font-size: 14px;
            line-height: 1.5;
            color: #555;
            word-break: break-word;
          }

          .network-ok-btn {
            width: 100%;
            height: 48px;
            border: none;
            border-radius: 12px;
            background: #005CA9;
            color: #FF6B00;
            font-size: 16px;
            font-weight: 700;
            cursor: pointer;
            transition: 0.2s ease;
            box-shadow: 0 5px 14px rgba(0, 92, 169, 0.25);
          }

          .network-ok-btn:hover {
            background: #004C8C;
            transform: translateY(-1px);
          }

          .network-ok-btn:active {
            transform: scale(0.98);
          }

          @keyframes networkPopup {
            from {
              opacity: 0;
              transform: scale(0.92);
            }

            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          @media (max-width: 480px) {
            .network-screen-wrapper {
              padding: 16px;
            }

            .network-modal-card {
              width: 90%;
              padding: 24px 18px;
            }

            .network-title {
              font-size: 18px;
            }

            .network-message {
              font-size: 13px;
            }

            .network-ok-btn {
              height: 45px;
              font-size: 15px;
            }
          }

          @media (min-width: 768px) {
            .network-modal-card {
              max-width: 400px;
            }
          }
        `}</style>
      </>
    );
  }

  // =====================================================
  // NO USER
  // =====================================================

  if (!user) {
    return (
      <>
        <div className="show-data-page">
          <div className="show-data-card">
            <h2>No user data found</h2>

            {/* NEW BUTTON */}
            {userNotFound && (
              <button
                type="button"
                onClick={handleGoHome}
                style={{
                  marginTop: "12px",
                  background: "#FF6B00",
                  color: "#005CA9",
                  border: "none",
                  borderRadius: "10px",
                  padding: "11px 16px",
                  fontSize: "14px",
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  width: "100%",
                }}
              >
                Go to Home / Register
              </button>
            )}
          </div>
        </div>
      </>
    );
  }

  // =====================================================
  // PROFILE IMAGE
  // =====================================================

  const profileImage =
    user.profile_url ||
    user.profile_user ||
    localStorage.getItem("profile_user") ||
    "";

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div
      className={`${styles.sliderOverlay} ${
        isOpen ? styles.open : ""
      }`}
      aria-hidden={!isOpen}
    >
      {/* =================================================
          BACKDROP
      ================================================= */}

      <div
        className={styles.backdrop}
        onClick={onClose}
        aria-label="Close navigation"
      />

      {/* =================================================
          DRAWER
      ================================================= */}

      <aside
        className={styles.drawerContainer}
        role="dialog"
        aria-modal="true"
        aria-label="Partner navigation menu"
      >
        <div className={styles.scrollContent}>

          {/* =================================================
              TOP HEADER
          ================================================= */}

          <div className={styles.logoWrapper}>

            <div className={styles.brandLogo}>


              <span className={styles.brandText}>
                Bro
                <span
                  className={
                    styles.brandTextAccent
                  }
                >
                  Bizee
                </span>
              </span>

            </div>

            {/* CLOSE BUTTON */}

            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close menu"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line
                  x1="6"
                  y1="6"
                  x2="18"
                  y2="18"
                />

                <line
                  x1="18"
                  y1="6"
                  x2="6"
                  y2="18"
                />
              </svg>
            </button>

          </div>

          {/* =================================================
              USER PROFILE
          ================================================= */}

          <div
            className={styles.profileSection}
          >

            <div className={styles.avatarRing}>

              {profileImage ? (
                <img
                  src={profileImage}
                  alt={
                    user.name ||
                    "Profile"
                  }
                  className={
                    styles.avatarImage
                  }
                  onError={(event) => {
                    event.currentTarget.style.display =
                      "none";
                  }}
                />
              ) : (
                <div
                  className={
                    styles.avatarFallback
                  }
                >
                  {(
                    user.name ||
                    user.mobile_number ||
                    "B"
                  )
                    .charAt(0)
                    .toUpperCase()}
                </div>
              )}

            </div>

            <h2
              className={styles.userName}
            >
              {user.name ||
                user.mobile_number ||
                "-"}
            </h2>

            {user.mobile_number && (
              <p
                className={
                  styles.userMobile
                }
              >
               
              </p>
            )}

          </div>

          {/* =================================================
              NAVIGATION
          ================================================= */}

          <nav
            className={styles.navMenu}
            aria-label="Partner navigation"
          >
            {navItems.map((item) => {
              const completePath =
                basePath
                  ? `${basePath}${item.path}`
                  : item.path;

              const isActive =
                location.pathname ===
                  completePath ||
                location.pathname.startsWith(
                  `${completePath}/`
                );

              return (
                <button
                  key={item.path}
                  type="button"
                  className={`
                    ${styles.navItem}
                    ${
                      isActive
                        ? styles.active
                        : ""
                    }
                  `}
                  onClick={() =>
                    handleNavClick(item)
                  }
                  aria-current={
                    isActive
                      ? "page"
                      : undefined
                  }
                >

                  {/* ICON */}

                  <span
                    className={
                      styles.navIcon
                    }
                  >
                    {item.icon}
                  </span>

                  {/* LABEL */}

                  <span
                    className={
                      styles.navLabel
                    }
                  >
                    {item.label}
                  </span>

                  {/* ARROW */}

                  <span
                    className={
                      styles.navArrow
                    }
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </span>

                </button>
              );
            })}
          </nav>

          {/* =================================================
              DIVIDER
          ================================================= */}

          <div
            className={
              styles.sectionDivider
            }
          />

          {/* =================================================
              VERSION
          ================================================= */}

          <div
            className={styles.versionText}
          >
            <span>BroBizee</span>
            <span>Version 0.0.1</span>
          </div>

          {/* =================================================
              LOGOUT
          ================================================= */}

          <div
            className={styles.actionSection}
          >
            <button
              type="button"
              className={
                styles.logoutButton
              }
              onClick={handleLogout}
            >

              <span
                className={
                  styles.logoutIcon
                }
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />

                  <polyline points="16 17 21 12 16 7" />

                  <line
                    x1="21"
                    y1="12"
                    x2="9"
                    y2="12"
                  />
                </svg>
              </span>

              <span>Logout</span>

            </button>
          </div>

          {/* =================================================
              FOOTER ACTIONS
          ================================================= */}

          <div
            className={
              styles.footerActions
            }
          >

            {/* RATE US */}

            <button
              type="button"
              className={
                styles.footerBtn
              }
              onClick={() => {
                alert(
                  "Thank you for using BroBizee!"
                );
              }}
            >

              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>

              <span>Rate Us</span>

            </button>

            {/* SHARE */}

            <button
              type="button"
              className={
                styles.footerBtn
              }
              onClick={async () => {
                const shareData = {
                  title: "BroBizee",
                  text: "Check out BroBizee!",
                  url: window.location.origin,
                };

                try {
                  if (
                    navigator.share
                  ) {
                    await navigator.share(
                      shareData
                    );
                  } else {
                    await navigator.clipboard.writeText(
                      window.location.origin
                    );

                    alert(
                      "BroBizee link copied!"
                    );
                  }
                } catch (shareError) {
                  console.log(
                    "Share cancelled:",
                    shareError
                  );
                }
              }}
            >

              <svg
                width="19"
                height="19"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle
                  cx="18"
                  cy="5"
                  r="3"
                />

                <circle
                  cx="6"
                  cy="12"
                  r="3"
                />

                <circle
                  cx="18"
                  cy="19"
                  r="3"
                />

                <line
                  x1="8.59"
                  y1="13.51"
                  x2="15.42"
                  y2="17.49"
                />

                <line
                  x1="15.41"
                  y1="6.51"
                  x2="8.59"
                  y2="10.49"
                />
              </svg>

              <span>Share</span>

            </button>

          </div>

        </div>
      </aside>
    </div>
  );
}